import { Component, Output, EventEmitter, signal, inject, effect, computed, untracked } from '@angular/core';

import { RankingObject } from '@shared/models/recurring-ranking.model';
import { User } from '@shared/models/user.model';
import { UserService } from '@shared/services/user.service';
import { RecurringRankigService } from '@shared/services/recurring-ranking.service';
import { MockDataService } from '@shared/services/mockData.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { RankingItemComponent } from './ranking-item/ranking-item.component';
import { SharedModule } from '@app/shared/shared.module';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'slotcom-recurring-ranking',
  templateUrl: './recurring-ranking.component.html',
  styleUrls: ['./recurring-ranking.component.scss'],
  standalone: true,
  imports: [RankingItemComponent, SharedModule],
})
export class RecurringRankingComponent {
  private readonly userService = inject(UserService);
  private readonly logger = inject(LoggerService);
  private readonly recurringRankingService = inject(RecurringRankigService);
  private readonly processDataService = inject(MockDataService);

  @Output()
  rankingDisabled = new EventEmitter<boolean>();

  readonly ranking = computed(() => this.rankingObject());

  readonly getRanking = toSignal(this.recurringRankingService.getRecurringRanking(), {
    initialValue: null,
  });

  readonly rankingObject = computed(() => {
    const apiRanking = this.getRanking();

    let fakeRanking: RankingObject;

    if (!apiRanking) {
      fakeRanking = this.processDataService.getMockedRecurringRanking(false);
      return fakeRanking;
    }

    if (apiRanking.isActive === false) {
      fakeRanking = this.processDataService.getMockedRecurringRanking(apiRanking.isActive);
      return fakeRanking;
    }

    if (apiRanking?.trophyRanking?.length == 0) {
      fakeRanking = this.processDataService.getMockedRecurringRanking(apiRanking.isActive);
      return fakeRanking;
    }

    return apiRanking;
  });

  readonly hasTrophies = computed(() => {
    const ranking = this.getRanking();
    return ranking?.trophyRanking?.length > 0;
  });

  readonly currentUser = computed(() => {
    const ranking = this.rankingObject();
    return ranking?.trophyRanking.find((r) => r.userId === this.userId);
  });

  readonly disabled = computed(() => {
    const ranking = this.rankingObject();
    return !ranking?.isActive;
  });

  readonly ids = computed(() => {
    const user = this.currentUser();
    if (!user) return null;

    return {
      current: `ranking-item-${user.rankingPosition}`,
      next: `ranking-item-${user.rankingPosition + 1}`,
      prev: `ranking-item-${user.rankingPosition - 1}`,
    };
  });

  readonly fontSize = computed(() => {
    return this.hasTrophies() ? 14 : 12;
  });

  readonly loading = computed(() => this.getRanking() === null);

  userId: string;

  container: HTMLElement | null = null;

  showFloatingUser = signal(false);
  stickyTop = signal(false);
  stickyBottom = signal(false);

  constructor() {
    effect(() => {
      const disabled = this.disabled();

      untracked(() => {
        this.rankingDisabled.emit(disabled);
      });
    });

    effect(() => {
      if (this.rankingObject() && this.currentUser()) {
        queueMicrotask(() => this.scroll());
      }
    });

    this.userService.currentUser$.subscribe((user: User) => {
      this.userId = user.id;
    });
  }

  public scroll() {
    const ids = this.ids();
    if (!ids || this.disabled()) return;

    this.container = document.getElementById('ranking-container');

    const el = document.getElementById(ids.current);
    if (!el || !this.container) return;

    const userRect = el.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();

    const fullyVisible = userRect.top >= containerRect.top && userRect.bottom <= containerRect.bottom;

    if (fullyVisible) return this.setNonSticky();
    if (userRect.top < containerRect.top) return this.setStickyTop();
    if (userRect.bottom > containerRect.bottom) return this.setStickyBottom();

    this.setNonSticky();
  }

  private setStickyTop() {
    this.stickyTop.set(true);
    this.stickyBottom.set(false);
    this.showFloatingUser.set(true);
  }

  private setStickyBottom() {
    this.stickyBottom.set(true);
    this.stickyTop.set(false);
    this.showFloatingUser.set(true);
  }

  private setNonSticky() {
    this.stickyTop.set(false);
    this.stickyBottom.set(false);
    this.showFloatingUser.set(false);
  }
}
